import type { FC, ReactNode } from 'react'
import type { ModuleId } from '@/types'

import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { MODULE_ICONS } from '@/components/landing/module-icons'

import {
    Award,
    Bell,
    CalendarDays,
    CircleCheck,
    Cpu,
    Download,
    Eye,
    FileText,
    Flag,
    Flame,
    GraduationCap,
    Hammer,
    Search,
    Sparkles,
    Swords,
    TrendingUp,
    Trophy,
    Users,
    Zap
} from 'lucide-react'

import { PHASES } from '@/constants/campus'

interface LaptopScreenProps {
    active: ModuleId
    onSelect: (id: ModuleId) => void
}

const SIDEBAR_ITEMS: Array<{ id: ModuleId, label: string }> = [
    { id: 'student-hub', label: "Dashboard" },
    { id: 'journey', label: "32-Month Journey" },
    { id: 'study-vault', label: "GKV Study Vault" },
    { id: 'placements', label: "Placements" },
    { id: 'marketplace', label: "Marketplace & Clubs" }
]

const STATIC_ITEMS = [
    { label: "Events & Workshops", icon: Sparkles },
    { label: "Calendar", icon: CalendarDays },
    { label: "Awards", icon: Award },
    { label: "Leaderboard", icon: Trophy }
]

const Panel: FC<{ className?: string, children: ReactNode }> = ({ className, children }): ReactNode => (
    <div className={cn('rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)]', className)}>
        {children}
    </div>
)

const Chip: FC<{ className?: string, children: ReactNode }> = ({ className, children }): ReactNode => (
    <span className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-semibold', className)}>
        {children}
    </span>
)

const Sidebar: FC<LaptopScreenProps> = ({ active, onSelect }): ReactNode => (
    <aside className='flex w-[232px] shrink-0 flex-col justify-between border-r border-slate-200 bg-white'>
        <div>
            <div className='flex items-center gap-2.5 border-b border-slate-100 px-4 py-4'>
                <Logo size={34} />

                <div className='min-w-0 leading-tight'>
                    <div className='truncate text-[13px] font-bold'>
                        Gurukul Engineers Hub
                    </div>

                    <div className='text-[10px] text-slate-400'>
                        GKV FET · Haridwar
                    </div>
                </div>
            </div>

            <p className='px-5 pb-1.5 pt-4 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400'>
                Workspace
            </p>

            <nav className='space-y-0.5 px-3'>
                {SIDEBAR_ITEMS.map(item => {
                    const Icon = MODULE_ICONS[item.id]
                    const selected = item.id === active

                    return (
                        <button
                            key={item.id}
                            type='button'
                            tabIndex={-1}
                            onClick={() => onSelect(item.id)}
                            className={cn(
                                'relative flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] transition-colors duration-300',
                                selected ? 'bg-slate-900 font-semibold text-white shadow-sm' : 'font-medium text-slate-600 hover:bg-slate-100'
                            )}
                        >
                            <Icon className='relative size-4' />

                            <span className='relative'>
                                {item.label}
                            </span>
                        </button>
                    )
                })}
            </nav>

            <p className='px-5 pb-1.5 pt-5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400'>
                Campus
            </p>

            <div className='space-y-0.5 px-3'>
                {STATIC_ITEMS.map(item => (
                    <div key={item.label} className='flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-slate-500'>
                        <item.icon className='size-4' />

                        {item.label}
                    </div>
                ))}
            </div>
        </div>

        <div className='m-3 rounded-xl border border-slate-200 bg-slate-50 p-3'>
            <div className='flex items-center gap-2.5'>
                <span className='grid size-9 place-items-center rounded-lg bg-gradient-to-br from-indigo-500 to-emerald-500 text-[12px] font-bold text-white'>
                    AS
                </span>

                <div className='min-w-0'>
                    <div className='truncate text-[12px] font-bold'>
                        Aarav Sharma
                    </div>

                    <div className='text-[10px] text-slate-500'>
                        CSE · 23FET1042
                    </div>
                </div>
            </div>

            <div className='mt-2 flex gap-1'>
                <Chip className='bg-slate-900 text-white'>B.Tech Student</Chip>
                <Chip className='bg-blue-100 text-blue-800'>Year 2</Chip>
            </div>
        </div>
    </aside>
)

const TopBar: FC<{ active: ModuleId }> = ({ active }): ReactNode => (
    <div className='flex h-[60px] shrink-0 items-center justify-between border-b border-slate-200 bg-white/90 px-6'>
        <div className='flex w-[380px] items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-400'>
            <Search className='size-4' />

            Search events, PYQs, drives and students

            <kbd className='ml-auto rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200'>⌘K</kbd>
        </div>

        <div className='flex items-center gap-3'>
            <Chip className='bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'>
                <span className='size-1.5 animate-pulse rounded-full bg-emerald-500' />
                {active === 'placements' ? '3 drives live' : 'Week 14 live'}
            </Chip>

            <span className='relative grid size-9 place-items-center rounded-xl border border-slate-200 text-slate-500'>
                <Bell className='size-4' />
                <span className='absolute right-2 top-2 size-1.5 rounded-full bg-rose-500' />
            </span>

            <span className='grid size-9 place-items-center rounded-xl bg-slate-900 text-[12px] font-bold text-white'>
                AS
            </span>
        </div>
    </div>
)

const KPI_CARDS = [
    { color: '#EF4444', title: "Events", value: '12', icon: Sparkles, chips: [["2 Draft", 'bg-red-50 text-red-600'], ["6 Live", 'bg-blue-50 text-blue-700'], ["4 Completed", 'bg-emerald-50 text-emerald-700']] },
    { color: '#3B82F6', title: "Hiring Pipeline", value: '48', icon: Users, chips: [["26 Applied", 'bg-slate-100 text-slate-600'], ["14 Shortlisted", 'bg-slate-100 text-slate-600']] },
    { color: '#8B5CF6', title: "Student Engagement", value: '312', icon: TrendingUp, bar: 86, barColor: 'bg-violet-500', note: "Joined an event: 268 of 312" },
    { color: '#10B981', title: "Placement Totals", value: '31', icon: GraduationCap, bar: 64, barColor: 'bg-emerald-500', note: "31 offer / joined of 48" },
    { color: '#F59E0B', title: "Club Chapters", value: '4', icon: CalendarDays, note: "4 branch chapters · 312 members" }
]

const READINESS = [
    { label: "Aptitude", value: 82 },
    { label: "Coding / Core", value: 76 },
    { label: "Problem Solving", value: 80 },
    { label: "Communication", value: 71 },
    { label: "Practical Innovation", value: 79 }
]

const BRANCH_GROUPS = [
    { name: "Software Engineering", tag: "CSE / IT", event: "AI / ML Foundations", week: 13, color: 'from-blue-500 to-indigo-600', joined: 92 },
    { name: "Civil & Core", tag: "Core", event: "Green Infrastructure Discussion", week: 14, color: 'from-amber-400 to-orange-500', joined: 64 },
    { name: "Mechanical Engineering", tag: "ME", event: "Robotics Fundamentals", week: 15, color: 'from-emerald-500 to-emerald-700', joined: 78 },
    { name: "Electrical / Electronics", tag: "ECE / EE", event: "IoT and Renewable Energy", week: 16, color: 'from-purple-500 to-pink-500', joined: 85 }
]

const CURRENT_MONTH = 14

const JourneyCells: FC = (): ReactNode => (
    <div>
        <div className='grid grid-cols-[repeat(32,minmax(0,1fr))] gap-[3px]'>
            {Array.from({ length: 32 }, (_, index) => {
                const month = index + 1
                const phase = PHASES.find(item => month >= item.from && month <= item.to) ?? PHASES[0]
                const done = month < CURRENT_MONTH
                const current = month === CURRENT_MONTH

                return (
                    <motion.span
                        key={month}
                        initial={{ opacity: 0, scaleY: 0.4 }}
                        animate={{ opacity: done || current ? 1 : 0.16, scaleY: 1 }}
                        transition={{ delay: index * 0.015, duration: 0.3 }}
                        className={cn('relative h-8 rounded-[5px]', current && 'ring-2 ring-offset-2 ring-offset-white')}
                        style={{ backgroundColor: phase.color, ['--tw-ring-color' as string]: phase.color }}
                    >
                        {[9, 18, 29, 32].includes(month) && (
                            <Flag className={cn('absolute -top-5 left-1/2 size-3.5 -translate-x-1/2', month < CURRENT_MONTH ? 'text-emerald-500' : 'text-amber-500')} />
                        )}
                    </motion.span>
                )
            })}
        </div>

        <div className='mt-2 grid grid-cols-[9fr_11fr_9fr_3fr] gap-[3px] text-[10px] font-semibold text-slate-500'>
            {PHASES.map(phase => (
                <span key={phase.id} className='truncate border-t-2 pt-1.5' style={{ borderColor: phase.color }}>
                    {phase.name}
                </span>
            ))}
        </div>
    </div>
)

const ReadinessGauge: FC<{ score: number, size?: number }> = ({ score, size = 128 }): ReactNode => {
    const radius = 52
    const circumference = 2 * Math.PI * radius

    return (
        <div className='relative grid place-items-center' style={{ width: size, height: size }}>
            <svg viewBox='0 0 128 128' className='absolute inset-0 -rotate-90'>
                <circle cx='64' cy='64' r={radius} fill='none' stroke='#EEF2FF' strokeWidth='11' />
                <motion.circle
                    cx='64'
                    cy='64'
                    r={radius}
                    fill='none'
                    stroke='url(#gauge-gradient)'
                    strokeWidth='11'
                    strokeLinecap='round'
                    strokeDasharray={circumference}
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset: circumference * (1 - score / 100) }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                />
                <defs>
                    <linearGradient id='gauge-gradient' x1='0' y1='0' x2='1' y2='1'>
                        <stop offset='0%' stopColor='#6366F1' />
                        <stop offset='100%' stopColor='#10B981' />
                    </linearGradient>
                </defs>
            </svg>

            <div className='text-center leading-none'>
                <div className='text-[34px] font-extrabold tracking-tight'>
                    {score}
                </div>

                <div className='mt-1 text-[11px] font-semibold text-slate-400'>
                    / 100
                </div>
            </div>
        </div>
    )
}

const DashboardScreen: FC = (): ReactNode => (
    <div className='space-y-4'>
        <div className='flex items-end justify-between'>
            <div>
                <h3 className='text-[22px] font-bold tracking-tight'>
                    Welcome back, Aarav
                </h3>

                <p className='mt-0.5 text-[12px] text-slate-500'>
                    Year 2 · Computer Science · Phase 2 · Skill Refinement
                </p>
            </div>

            <div className='flex gap-2'>
                <Chip className='bg-amber-50 px-2.5 py-1 text-amber-700 ring-1 ring-amber-200'>
                    <Flag className='size-3' />
                    Month 18 milestone in 4 months
                </Chip>
            </div>
        </div>

        <div className='grid grid-cols-5 gap-3'>
            {KPI_CARDS.map((card, index) => (
                <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index }}
                    whileHover={{ y: -3 }}
                    className='rounded-xl border border-t-4 border-slate-200 bg-white p-3.5 shadow-[0_1px_3px_rgba(15,23,42,0.05)]'
                    style={{ borderTopColor: card.color }}
                >
                    <div className='flex items-center justify-between'>
                        <span className='text-[11px] font-semibold text-slate-700'>
                            {card.title}
                        </span>

                        <card.icon className='size-4' style={{ color: card.color }} />
                    </div>

                    <div className='my-1.5 text-[28px] font-bold leading-none tracking-tight'>
                        {card.value}
                    </div>

                    {card.chips && (
                        <div className='flex flex-wrap gap-1'>
                            {card.chips.map(([label, className]) => (
                                <Chip key={label} className={className}>{label}</Chip>
                            ))}
                        </div>
                    )}

                    {card.bar !== undefined && (
                        <div className='h-1.5 overflow-hidden rounded-full bg-slate-100'>
                            <motion.div initial={{ width: 0 }} animate={{ width: `${card.bar}%` }} transition={{ duration: 0.9, delay: 0.2 }} className={cn('h-full rounded-full', card.barColor)} />
                        </div>
                    )}

                    {card.note && (
                        <p className='mt-1.5 text-[10.5px] text-slate-500'>
                            {card.note}
                        </p>
                    )}
                </motion.div>
            ))}
        </div>

        <div className='grid grid-cols-12 gap-4'>
            <Panel className='col-span-7 p-5'>
                <div className='mb-7 flex items-center justify-between'>
                    <div>
                        <div className='text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500'>
                            32-Month Club Journey
                        </div>

                        <div className='mt-1 text-[15px] font-bold'>
                            Month 14 · Skill Refinement in progress
                        </div>
                    </div>

                    <Chip className='bg-indigo-50 px-2.5 py-1 text-indigo-700'>44% complete</Chip>
                </div>

                <JourneyCells />

                <div className='mt-4 grid grid-cols-4 gap-2 text-[10.5px]'>
                    {PHASES.map(phase => (
                        <div key={phase.id} className='rounded-lg bg-slate-50 px-2.5 py-2'>
                            <div className='font-semibold text-slate-700'>
                                {phase.milestone.split(' · ')[0]}
                            </div>

                            <div className='truncate text-slate-500'>
                                {phase.milestone.split(' · ')[1]}
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>

            <Panel className='col-span-5 p-5'>
                <div className='flex items-start justify-between'>
                    <div>
                        <div className='text-[15px] font-bold'>
                            My 5-Skill Readiness
                        </div>

                        <div className='text-[11px] text-slate-500'>
                            Latest T&P assessment
                        </div>
                    </div>

                    <Chip className='bg-emerald-50 text-emerald-700'>Top 10%</Chip>
                </div>

                <div className='mt-3 flex items-center gap-5'>
                    <ReadinessGauge score={78} size={118} />

                    <div className='flex-1 space-y-2'>
                        {READINESS.map((skill, index) => (
                            <div key={skill.label}>
                                <div className='flex justify-between text-[10.5px] font-medium text-slate-600'>
                                    <span>{skill.label}</span>
                                    <span className='font-bold text-ink'>{skill.value}</span>
                                </div>

                                <div className='mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100'>
                                    <motion.div initial={{ width: 0 }} animate={{ width: `${skill.value}%` }} transition={{ duration: 0.8, delay: 0.1 * index }} className='h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500' />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Panel>
        </div>

        <div className='grid grid-cols-12 gap-4'>
            <Panel className='col-span-7 p-4'>
                <div className='mb-3 flex items-center justify-between'>
                    <div className='text-[15px] font-bold'>
                        Branch Groups · This Month
                    </div>

                    <span className='text-[11px] text-slate-400'>Rotates weekly</span>
                </div>

                <div className='space-y-2.5'>
                    {BRANCH_GROUPS.map(group => (
                        <div key={group.name} className='flex items-center gap-3'>
                            <span className={cn('size-8 shrink-0 rounded-lg bg-gradient-to-br', group.color)} />

                            <div className='min-w-0 flex-1'>
                                <div className='flex items-center gap-2 text-[12px] font-semibold'>
                                    {group.name}
                                    <span className='text-[10px] font-medium text-slate-400'>{group.tag}</span>
                                </div>

                                <div className='truncate text-[11px] text-slate-500'>
                                    Week {group.week} · {group.event}
                                </div>
                            </div>

                            <div className='w-24'>
                                <div className='h-1.5 overflow-hidden rounded-full bg-slate-100'>
                                    <div className={cn('h-full rounded-full bg-gradient-to-r', group.color)} style={{ width: `${group.joined}%` }} />
                                </div>

                                <div className='mt-1 text-right text-[10px] text-slate-500'>
                                    {group.joined}% joined
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>

            <Panel className='col-span-5 p-4'>
                <div className='mb-3 text-[15px] font-bold'>
                    Coming up this week
                </div>

                <div className='space-y-2'>
                    {[
                        { day: 'FRI', title: "Coding Challenge · Arrays & Strings", meta: "Software · Lab 3", tone: 'bg-blue-50 text-blue-700' },
                        { day: 'SAT', title: "Mock HR Interviews", meta: "T&P Cell · Seminar Hall", tone: 'bg-emerald-50 text-emerald-700' },
                        { day: 'MON', title: "Innovation Fair registrations close", meta: "Month 18 milestone", tone: 'bg-amber-50 text-amber-700' }
                    ].map(item => (
                        <div key={item.title} className='flex items-center gap-3 rounded-xl border border-slate-100 p-2'>
                            <span className={cn('grid size-9 shrink-0 place-items-center rounded-lg text-[10px] font-extrabold', item.tone)}>
                                {item.day}
                            </span>

                            <div className='min-w-0'>
                                <div className='truncate text-[12px] font-semibold'>
                                    {item.title}
                                </div>

                                <div className='text-[10.5px] text-slate-500'>
                                    {item.meta}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>
        </div>
    </div>
)

const JOURNEY_PHASES = [
    { status: 'Completed', focus: "Aptitude, C / Python basics and confidence-building events", items: ["Aptitude Assessments", "Coding Challenges", "Mock Technical Interviews"] },
    { status: 'Active', focus: "Branch tracks: AI / Web, IoT, Circuits, CAD / Robotics", items: ["Full-stack & AI / ML workshops", "IoT & advanced circuit design", "CAD modeling & robotics"] },
    { status: 'Upcoming', focus: "Career prep with leadership and soft skills", items: ["Resume Writing Workshops", "Alumni Talks", "Mock Placement Days"] },
    { status: 'Upcoming', focus: "Intensive placement prep and mentorship", items: ["Domain-Specific Tests", "1-on-1 Mentorship", "Legacy Grand Finale"] }
]

const ROTATION = [
    { week: 13, group: "Software", title: "AI / ML Foundations", type: "Technical", color: '#4F46E5' },
    { week: 14, group: "Civil & Core", title: "Green Infrastructure Discussion", type: "Group Discussion", color: '#F59E0B' },
    { week: 15, group: "Mechanical", title: "Robotics Fundamentals", type: "Technical", color: '#059669' },
    { week: 16, group: "Electrical / Electronics", title: "IoT and Renewable Energy", type: "Group Discussion", color: '#C026D3' }
]

const JourneyScreen: FC = (): ReactNode => (
    <div className='space-y-4'>
        <div className='flex items-end justify-between'>
            <div>
                <h3 className='text-[22px] font-bold tracking-tight'>
                    32-Month Club Journey
                </h3>

                <p className='mt-0.5 text-[12px] text-slate-500'>
                    Four phases, one seasonal milestone each, weekly challenges for every branch group
                </p>
            </div>

            <div className='flex gap-2'>
                <Chip className='bg-slate-900 px-2.5 py-1 text-white'>Month 14 of 32</Chip>
                <Chip className='bg-amber-50 px-2.5 py-1 text-amber-700 ring-1 ring-amber-200'>Next: Month 18</Chip>
            </div>
        </div>

        <div className='grid grid-cols-4 gap-3'>
            {PHASES.map((phase, index) => {
                const meta = JOURNEY_PHASES[index]

                return (
                    <motion.div
                        key={phase.id}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.07 }}
                        className={cn('relative overflow-hidden rounded-2xl border bg-white p-4', meta.status === 'Active' ? 'border-emerald-300 shadow-[0_10px_30px_-14px_rgba(16,185,129,0.5)]' : 'border-slate-200')}
                    >
                        <span className='absolute inset-x-0 top-0 h-1' style={{ backgroundColor: phase.color }} />

                        <div className='flex items-center justify-between'>
                            <span className='text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400'>
                                Phase {phase.id}
                            </span>

                            <Chip className={meta.status === 'Completed' ? 'bg-emerald-50 text-emerald-700' : meta.status === 'Active' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-500'}>
                                {meta.status}
                            </Chip>
                        </div>

                        <div className='mt-2 text-[15px] font-bold'>
                            {phase.name}
                        </div>

                        <div className='text-[11px] font-medium text-slate-500'>
                            {phase.months}
                        </div>

                        <p className='mt-2 text-[11px] leading-snug text-slate-600'>
                            {meta.focus}
                        </p>

                        <ul className='mt-2.5 space-y-1.5'>
                            {meta.items.map(item => (
                                <li key={item} className='flex items-center gap-1.5 text-[11px] text-slate-700'>
                                    <CircleCheck className={cn('size-3.5 shrink-0', meta.status === 'Completed' ? 'text-emerald-500' : 'text-slate-300')} />
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <div className='mt-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[10.5px] font-semibold text-amber-800'>
                            <Flag className='size-3' />
                            {phase.milestone}
                        </div>

                        {meta.status === 'Active' && (
                            <div className='mt-3'>
                                <div className='h-1.5 overflow-hidden rounded-full bg-emerald-100'>
                                    <motion.div initial={{ width: 0 }} animate={{ width: '45%' }} transition={{ duration: 1 }} className='h-full rounded-full bg-emerald-500' />
                                </div>

                                <div className='mt-1 text-[10px] text-slate-500'>
                                    5 of 11 months done
                                </div>
                            </div>
                        )}
                    </motion.div>
                )
            })}
        </div>

        <Panel className='p-5'>
            <div className='mb-4 flex items-center justify-between'>
                <div>
                    <div className='text-[15px] font-bold'>
                        Weekly branch rotation · Weeks 13–16
                    </div>

                    <div className='text-[11px] text-slate-500'>
                        Every group gets its own challenge each month, so no branch is left waiting
                    </div>
                </div>

                <Chip className='bg-indigo-50 text-indigo-700'>32 weeks per cycle</Chip>
            </div>

            <div className='relative grid grid-cols-4 gap-3'>
                <div className='absolute left-6 right-6 top-[18px] h-[2px] bg-[repeating-linear-gradient(90deg,#CBD5E1_0_8px,transparent_8px_14px)]' />

                {ROTATION.map((slot, index) => (
                    <div key={slot.week} className='relative'>
                        <span
                            className={cn('relative z-10 mx-auto grid size-9 place-items-center rounded-full border-2 bg-white text-[11px] font-extrabold', index === 1 && 'shadow-[0_0_0_6px_rgba(245,158,11,0.18)]')}
                            style={{ borderColor: slot.color, color: slot.color }}
                        >
                            W{slot.week}
                        </span>

                        <div className={cn('mt-3 rounded-xl border p-3', index === 1 ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-slate-50/60')}>
                            <div className='text-[10px] font-bold uppercase tracking-[0.1em]' style={{ color: slot.color }}>
                                {slot.group}
                            </div>

                            <div className='mt-1 text-[12px] font-semibold leading-snug'>
                                {slot.title}
                            </div>

                            <div className='mt-1.5 flex items-center justify-between text-[10px] text-slate-500'>
                                <span>{slot.type}</span>
                                {index === 1 && <span className='font-bold text-amber-700'>This week</span>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Panel>
    </div>
)

const VAULT_DOCS: Record<number, Array<{ subject: string, type: string, meta: string }>> = {
    1: [
        { subject: "Engineering Mathematics I", type: 'PYQ', meta: "End Sem 2024 · 6 pages" },
        { subject: "Programming for Problem Solving (C)", type: 'Lab Manual', meta: "12 experiments" },
        { subject: "Engineering Physics", type: 'Sessional', meta: "Sessional II · 2024" },
        { subject: "Basic Electrical Engineering", type: 'PYQ', meta: "End Sem 2023 · 4 pages" },
        { subject: "Engineering Graphics", type: 'Lab Manual', meta: "Drawing sheets 1–8" },
        { subject: "Aptitude Starter Sheet", type: 'Placement', meta: "120 questions" }
    ],
    2: [
        { subject: "Engineering Mathematics II", type: 'PYQ', meta: "End Sem 2024 · 6 pages" },
        { subject: "Python Programming", type: 'Lab Manual', meta: "10 experiments" },
        { subject: "Engineering Chemistry", type: 'Sessional', meta: "Sessional I · 2024" },
        { subject: "Basic Electronics", type: 'PYQ', meta: "End Sem 2023 · 5 pages" },
        { subject: "Workshop Practice", type: 'Lab Manual', meta: "Fitting, welding, carpentry" },
        { subject: "Verbal Ability Sheet", type: 'Placement', meta: "80 questions" }
    ],
    3: [
        { subject: "Data Structures", type: 'PYQ', meta: "End Sem 2024 · 5 pages" },
        { subject: "Digital Logic Design", type: 'PYQ', meta: "End Sem 2023 · 4 pages" },
        { subject: "Discrete Mathematics", type: 'Sessional', meta: "Sessional II · 2024" },
        { subject: "OOP with C++", type: 'Lab Manual', meta: "14 experiments" },
        { subject: "Computer Organization", type: 'Sessional', meta: "Sessional I · 2024" },
        { subject: "DSA Interview Sheet", type: 'Placement', meta: "150 problems" }
    ],
    4: [
        { subject: "Design & Analysis of Algorithms", type: 'PYQ', meta: "End Sem 2024 · 6 pages" },
        { subject: "Operating Systems", type: 'PYQ', meta: "End Sem 2023 · 5 pages" },
        { subject: "Database Management Systems", type: 'Lab Manual', meta: "SQL lab · 12 tasks" },
        { subject: "Theory of Computation", type: 'Sessional', meta: "Sessional II · 2024" },
        { subject: "Microprocessors", type: 'Lab Manual', meta: "8085 / 8086 lab" },
        { subject: "SQL Interview Sheet", type: 'Placement', meta: "60 questions" }
    ],
    5: [
        { subject: "Computer Networks", type: 'PYQ', meta: "End Sem 2024 · 5 pages" },
        { subject: "Software Engineering", type: 'Sessional', meta: "Sessional I · 2024" },
        { subject: "Compiler Design", type: 'PYQ', meta: "End Sem 2023 · 4 pages" },
        { subject: "Web Technologies", type: 'Lab Manual', meta: "HTML to React" },
        { subject: "Machine Learning", type: 'Lab Manual', meta: "9 notebooks" },
        { subject: "Core CS Interview Sheet", type: 'Placement', meta: "OS · CN · DBMS" }
    ],
    6: [
        { subject: "Artificial Intelligence", type: 'PYQ', meta: "End Sem 2024 · 5 pages" },
        { subject: "Cloud Computing", type: 'Sessional', meta: "Sessional II · 2024" },
        { subject: "Information Security", type: 'PYQ', meta: "End Sem 2023 · 4 pages" },
        { subject: "Data Mining", type: 'Lab Manual', meta: "WEKA & Python" },
        { subject: "Mobile App Development", type: 'Lab Manual', meta: "6 mini projects" },
        { subject: "Mock Test Pack", type: 'Placement', meta: "5 full tests" }
    ],
    7: [
        { subject: "Deep Learning", type: 'PYQ', meta: "End Sem 2024 · 5 pages" },
        { subject: "Distributed Systems", type: 'Sessional', meta: "Sessional I · 2024" },
        { subject: "Big Data Analytics", type: 'Lab Manual', meta: "Hadoop & Spark" },
        { subject: "Project Phase I", type: 'Sessional', meta: "Review rubric" },
        { subject: "System Design Basics", type: 'Placement', meta: "20 case studies" },
        { subject: "HR Interview Sheet", type: 'Placement', meta: "50 questions" }
    ],
    8: [
        { subject: "Internet of Things Systems", type: 'PYQ', meta: "End Sem 2024 · 4 pages" },
        { subject: "Blockchain Fundamentals", type: 'Sessional', meta: "Sessional II · 2024" },
        { subject: "Major Project", type: 'Lab Manual', meta: "Report template" },
        { subject: "Company-wise Question Bank", type: 'Placement', meta: "Updated by T&P" },
        { subject: "Group Discussion Topics", type: 'Placement', meta: "40 topics" },
        { subject: "Final Mock Test", type: 'Placement', meta: "Full length" }
    ]
}

const DOC_TYPES = [
    { value: 'All', label: "All" },
    { value: 'PYQ', label: "PYQs" },
    { value: 'Sessional', label: "Sessional Papers" },
    { value: 'Lab Manual', label: "Lab Manuals" },
    { value: 'Placement', label: "Placement Sheets" }
]

const DOC_TONES: Record<string, string> = {
    PYQ: 'bg-indigo-50 text-indigo-700',
    Sessional: 'bg-sky-50 text-sky-700',
    'Lab Manual': 'bg-emerald-50 text-emerald-700',
    Placement: 'bg-amber-50 text-amber-700'
}

const StudyVaultScreen: FC = (): ReactNode => {
    const [semester, setSemester] = useState(3)
    const [type, setType] = useState('All')

    const docs = VAULT_DOCS[semester].filter(doc => type === 'All' || doc.type === type)

    return (
        <div className='flex h-full gap-4'>
            <div className='min-w-0 flex-1 space-y-4'>
                <div className='flex items-end justify-between'>
                    <div>
                        <h3 className='text-[22px] font-bold tracking-tight'>
                            GKV Study Vault
                        </h3>

                        <p className='mt-0.5 text-[12px] text-slate-500'>
                            Semester-wise PYQs, sessional papers, lab manuals and placement sheets
                        </p>
                    </div>

                    <Chip className='bg-emerald-50 px-2.5 py-1 text-emerald-700 ring-1 ring-emerald-200'>
                        <CircleCheck className='size-3.5' />
                        Vault pass active
                    </Chip>
                </div>

                <Panel className='p-3'>
                    <div className='flex items-center gap-1.5'>
                        <span className='mr-1 text-[11px] font-semibold text-slate-500'>Semester</span>

                        {Array.from({ length: 8 }, (_, index) => index + 1).map(value => (
                            <button
                                key={value}
                                type='button'
                                tabIndex={-1}
                                onClick={() => setSemester(value)}
                                className={cn('grid size-8 place-items-center rounded-lg text-[12px] font-bold transition-colors duration-300', value === semester ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100')}
                            >
                                {value}
                            </button>
                        ))}

                        <span className='ml-auto rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600'>
                            Branch: CSE
                        </span>
                    </div>

                    <div className='mt-3 flex gap-1 rounded-lg bg-slate-100 p-1'>
                        {DOC_TYPES.map(option => (
                            <button
                                key={option.value}
                                type='button'
                                tabIndex={-1}
                                onClick={() => setType(option.value)}
                                className={cn('flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold transition', option.value === type ? 'bg-white text-ink shadow-sm' : 'text-slate-500 hover:text-ink')}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </Panel>

                <motion.div
                    key={`${semester}-${type}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className='grid grid-cols-3 gap-3'
                >
                    {docs.map(doc => (
                        <div
                            key={doc.subject}
                            className='group rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:border-indigo-300'
                        >
                            <div className='flex items-start justify-between'>
                                <span className='grid size-10 place-items-center rounded-xl bg-rose-50 text-rose-600'>
                                    <FileText className='size-5' />
                                </span>

                                <Chip className={DOC_TONES[doc.type]}>{doc.type}</Chip>
                            </div>

                            <div className='mt-3 line-clamp-2 min-h-[34px] text-[12.5px] font-semibold leading-snug'>
                                {doc.subject}
                            </div>

                            <div className='mt-1 text-[10.5px] text-slate-500'>
                                Sem {semester} · {doc.meta}
                            </div>

                            <div className='mt-3 flex gap-1.5'>
                                <span className='flex flex-1 items-center justify-center gap-1 rounded-lg border border-slate-200 py-1.5 text-[11px] font-semibold text-slate-600'>
                                    <Eye className='size-3.5' />
                                    Preview
                                </span>

                                <span className='flex flex-1 items-center justify-center gap-1 rounded-lg bg-slate-900 py-1.5 text-[11px] font-semibold text-white'>
                                    <Download className='size-3.5' />
                                    PDF
                                </span>
                            </div>
                        </div>
                    ))}

                    {!docs.length && (
                        <div className='col-span-3 rounded-2xl border border-dashed border-slate-300 p-10 text-center text-[12px] text-slate-500'>
                            No documents of this type in Semester {semester} yet.
                        </div>
                    )}
                </motion.div>
            </div>

            <div className='w-[260px] shrink-0 space-y-4'>
                <Panel className='p-4'>
                    <div className='text-[13px] font-bold'>
                        Exam countdown
                    </div>

                    <div className='mt-3 flex items-end gap-2'>
                        <span className='text-[40px] font-extrabold leading-none tracking-tight text-indigo-600'>
                            12
                        </span>

                        <span className='pb-1 text-[12px] font-medium text-slate-500'>
                            days to mid-sem
                        </span>
                    </div>

                    <div className='mt-3 h-1.5 overflow-hidden rounded-full bg-indigo-100'>
                        <div className='h-full w-[62%] rounded-full bg-indigo-500' />
                    </div>

                    <p className='mt-2 text-[10.5px] text-slate-500'>
                        You have revised 18 of 29 recommended papers
                    </p>
                </Panel>

                <Panel className='p-4'>
                    <div className='mb-2.5 text-[13px] font-bold'>
                        Recently opened
                    </div>

                    {["Data Structures · PYQ 2024", "OOP with C++ · Lab Manual", "DSA Interview Sheet"].map(item => (
                        <div key={item} className='flex items-center gap-2 border-t border-slate-100 py-2 text-[11.5px] text-slate-600 first:border-t-0'>
                            <FileText className='size-3.5 text-rose-500' />
                            <span className='truncate'>{item}</span>
                        </div>
                    ))}
                </Panel>

                <div className='rounded-2xl bg-gradient-to-br from-slate-900 to-indigo-950 p-4 text-white'>
                    <Sparkles className='size-4 text-amber-300' />

                    <p className='mt-2 text-[12px] font-semibold leading-snug'>
                        Curated by the Technical Head and checked against the GKV FET syllabus
                    </p>

                    <p className='mt-1 text-[10.5px] text-slate-300'>
                        312 documents across 8 semesters
                    </p>
                </div>
            </div>
        </div>
    )
}

const PIPELINE = [
    { stage: "Applied", count: 26, tone: '#94A3B8', people: [["Ishita Verma", "CSE", 81], ["Rohan Negi", "ME", 74]] },
    { stage: "Shortlisted", count: 14, tone: '#3B82F6', people: [["Priya Rawat", "ECE", 86], ["Kabir Joshi", "EE", 79]] },
    { stage: "Interview", count: 8, tone: '#8B5CF6', people: [["Ananya Bisht", "CSE", 91], ["Dev Malhotra", "ME", 83]] },
    { stage: "Offer", count: 5, tone: '#F59E0B', people: [["Sneha Kandpal", "CSE", 93]] },
    { stage: "Joined", count: 3, tone: '#10B981', people: [["Arjun Chauhan", "EE", 88]] }
] as const

const DRIVES = [
    { company: "Vertex Robotics", role: "Graduate Engineer Trainee", branches: "ME · EE", closes: "Closes in 3 days", initials: 'VR', color: 'from-indigo-500 to-blue-600' },
    { company: "Nimbus Analytics", role: "Associate Data Analyst", branches: "CSE · ECE", closes: "Closes in 6 days", initials: 'NA', color: 'from-emerald-500 to-teal-600' },
    { company: "Gridline Power", role: "Electrical Design Engineer", branches: "EE", closes: "Closes in 9 days", initials: 'GP', color: 'from-amber-500 to-orange-600' }
]

const PlacementsScreen: FC = (): ReactNode => (
    <div className='space-y-4'>
        <div className='flex items-end justify-between'>
            <div>
                <h3 className='text-[22px] font-bold tracking-tight'>
                    T&P Placements
                </h3>

                <p className='mt-0.5 text-[12px] text-slate-500'>
                    Readiness-ranked shortlists and a live hiring pipeline, approved by the T&P Cell
                </p>
            </div>

            <div className='flex gap-2'>
                <Chip className='bg-blue-50 px-2.5 py-1 text-blue-700'>48 candidates</Chip>
                <Chip className='bg-emerald-50 px-2.5 py-1 text-emerald-700'>64.5% placed</Chip>
            </div>
        </div>

        <div className='flex gap-4'>
            <Panel className='min-w-0 flex-1 p-4'>
                <div className='mb-3 flex items-center justify-between'>
                    <div className='text-[15px] font-bold'>
                        Hiring pipeline
                    </div>

                    <span className='text-[11px] text-slate-400'>Applied → Joined</span>
                </div>

                <div className='grid grid-cols-5 gap-2.5'>
                    {PIPELINE.map((column, columnIndex) => (
                        <div key={column.stage} className='rounded-xl bg-slate-50 p-2'>
                            <div className='mb-2 flex items-center justify-between px-1'>
                                <span className='flex items-center gap-1.5 text-[11px] font-bold'>
                                    <span className='size-2 rounded-full' style={{ backgroundColor: column.tone }} />
                                    {column.stage}
                                </span>

                                <span className='text-[11px] font-bold text-slate-400'>{column.count}</span>
                            </div>

                            <div className='space-y-2'>
                                {column.people.map(([name, branch, score], personIndex) => (
                                    <motion.div
                                        key={name}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: columnIndex * 0.08 + personIndex * 0.05 }}
                                        whileHover={{ y: -2 }}
                                        className='rounded-lg border border-slate-200 bg-white p-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]'
                                    >
                                        <div className='flex items-center gap-2'>
                                            <span className='grid size-7 shrink-0 place-items-center rounded-full bg-slate-900 text-[9.5px] font-bold text-white'>
                                                {name.split(' ').map(part => part[0]).join('')}
                                            </span>

                                            <div className='min-w-0'>
                                                <div className='truncate text-[11px] font-semibold'>{name}</div>
                                                <div className='text-[9.5px] text-slate-500'>{branch}</div>
                                            </div>
                                        </div>

                                        <div className='mt-2 flex items-center justify-between text-[9.5px]'>
                                            <span className='text-slate-500'>Readiness</span>
                                            <span className='font-bold text-indigo-600'>{score}</span>
                                        </div>
                                    </motion.div>
                                ))}

                                {column.count > column.people.length && (
                                    <div className='rounded-lg border border-dashed border-slate-300 py-1.5 text-center text-[10px] font-semibold text-slate-400'>
                                        +{column.count - column.people.length} more
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>

            <div className='w-[300px] shrink-0 space-y-4'>
                <Panel className='p-4'>
                    <div className='flex items-center justify-between'>
                        <div className='text-[13px] font-bold'>
                            5-Skill Readiness Index
                        </div>

                        <Chip className='bg-amber-50 text-amber-700'>Top 10%</Chip>
                    </div>

                    <div className='mt-3 space-y-2.5'>
                        {[["Sneha Kandpal", "CSE", 93], ["Ananya Bisht", "CSE", 91], ["Arjun Chauhan", "EE", 88], ["Priya Rawat", "ECE", 86]].map(([name, branch, score], index) => (
                            <div key={name} className='flex items-center gap-2.5'>
                                <span className={cn('grid size-6 place-items-center rounded-md text-[10px] font-extrabold', index === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500')}>
                                    {index + 1}
                                </span>

                                <div className='min-w-0 flex-1'>
                                    <div className='flex justify-between text-[11px]'>
                                        <span className='truncate font-semibold'>{name}</span>
                                        <span className='font-bold'>{score}</span>
                                    </div>

                                    <div className='mt-1 h-1 overflow-hidden rounded-full bg-slate-100'>
                                        <motion.div initial={{ width: 0 }} animate={{ width: `${score}%` }} transition={{ duration: 0.8, delay: index * 0.1 }} className='h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500' />
                                    </div>

                                    <div className='mt-0.5 text-[9.5px] text-slate-400'>{branch}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>

                <Panel className='p-4'>
                    <div className='mb-2.5 text-[13px] font-bold'>
                        Live campus drives
                    </div>

                    <div className='space-y-2'>
                        {DRIVES.map(drive => (
                            <div key={drive.company} className='flex items-center gap-2.5 rounded-xl border border-slate-100 p-2'>
                                <span className={cn('grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br text-[11px] font-extrabold text-white', drive.color)}>
                                    {drive.initials}
                                </span>

                                <div className='min-w-0 flex-1'>
                                    <div className='truncate text-[11.5px] font-semibold'>{drive.company}</div>
                                    <div className='truncate text-[10px] text-slate-500'>{drive.role} · {drive.branches}</div>
                                </div>

                                <span className='text-[9.5px] font-semibold text-rose-600'>{drive.closes.replace('Closes in ', '')}</span>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>
        </div>
    </div>
)

const LISTINGS = [
    { title: "Higher Engineering Mathematics (B.S. Grewal)", price: '₹350', category: 'Books', condition: "Like new · Sem 1–4", seller: "Meera P. · Year 3", icon: GraduationCap, tone: 'bg-indigo-50 text-indigo-600' },
    { title: "Engineering Drawing Kit + Mini Drafter", price: '₹450', category: 'Tools', condition: "Complete set", seller: "Vikram S. · Year 2", icon: Hammer, tone: 'bg-amber-50 text-amber-600' },
    { title: "Arduino Uno Starter Kit", price: '₹900', category: 'Electronics', condition: "Sensors included", seller: "Riya A. · Year 3", icon: Cpu, tone: 'bg-emerald-50 text-emerald-600' },
    { title: "Casio fx-991ES Plus Calculator", price: '₹650', category: 'Electronics', condition: "Exam approved", seller: "Kabir J. · Year 4", icon: Zap, tone: 'bg-sky-50 text-sky-600' },
    { title: "Lab Coat (M) + Safety Goggles", price: '₹250', category: 'Lab Gear', condition: "Used one semester", seller: "Ishita V. · Year 2", icon: Sparkles, tone: 'bg-rose-50 text-rose-600' },
    { title: "Digital Multimeter DT-830D", price: '₹300', category: 'Tools', condition: "Works perfectly", seller: "Rohan N. · Year 3", icon: Zap, tone: 'bg-violet-50 text-violet-600' }
]

const CATEGORIES = ['All', 'Books', 'Tools', 'Electronics', 'Lab Gear']

const MarketplaceScreen: FC = (): ReactNode => {
    const [category, setCategory] = useState('All')
    const items = LISTINGS.filter(item => category === 'All' || item.category === category)

    return (
        <div className='flex h-full gap-4'>
            <div className='min-w-0 flex-1 space-y-4'>
                <div className='flex items-end justify-between'>
                    <div>
                        <h3 className='text-[22px] font-bold tracking-tight'>
                            Campus Marketplace
                        </h3>

                        <p className='mt-0.5 text-[12px] text-slate-500'>
                            Verified GKV students only · meet on campus, pay in person
                        </p>
                    </div>

                    <span className='rounded-xl bg-slate-900 px-3.5 py-2 text-[12px] font-semibold text-white'>
                        + Sell an item
                    </span>
                </div>

                <div className='flex gap-1.5'>
                    {CATEGORIES.map(option => (
                        <button
                            key={option}
                            type='button'
                            tabIndex={-1}
                            onClick={() => setCategory(option)}
                            className={cn('rounded-full px-3.5 py-1.5 text-[11.5px] font-semibold ring-1 transition', option === category ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300')}
                        >
                            {option}
                        </button>
                    ))}
                </div>

                <motion.div
                    key={category}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className='grid grid-cols-3 gap-3'
                >
                    {items.map(item => (
                        <div
                            key={item.title}
                            className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)]'
                        >
                            <div className={cn('grid h-[92px] place-items-center', item.tone)}>
                                <item.icon className='size-9' strokeWidth={1.6} />
                            </div>

                            <div className='p-3'>
                                <div className='line-clamp-1 text-[12px] font-semibold'>{item.title}</div>
                                <div className='mt-0.5 text-[10.5px] text-slate-500'>{item.condition}</div>

                                <div className='mt-2.5 flex items-center justify-between'>
                                    <span className='text-[15px] font-extrabold'>{item.price}</span>
                                    <span className='rounded-lg bg-slate-900 px-2.5 py-1 text-[10.5px] font-semibold text-white'>Request</span>
                                </div>

                                <div className='mt-2 border-t border-slate-100 pt-2 text-[10px] text-slate-400'>{item.seller}</div>
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>

            <div className='w-[300px] shrink-0 space-y-4'>
                <div className='overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1628] via-[#13254A] to-[#1E1B4B] p-4 text-white'>
                    <div className='flex items-center justify-between'>
                        <Chip className='bg-rose-500/20 text-rose-200'>
                            <span className='size-1.5 animate-pulse rounded-full bg-rose-400' />
                            Live
                        </Chip>

                        <Swords className='size-4 text-amber-300' />
                    </div>

                    <div className='mt-3 text-[15px] font-bold leading-tight'>
                        Inter-College CodeSprint 2026
                    </div>

                    <div className='text-[11px] text-slate-300'>
                        Semi-finals · Round 3 of 4
                    </div>

                    <div className='mt-3 space-y-2'>
                        {[[["GKV FET Falcons", 412], ["Team Ganga Circuit", 388]], [["Doon Coders", 365], ["Shivalik Hackers", 371]]].map((match, index) => (
                            <div key={index} className='rounded-xl bg-white/[0.07] p-2.5 ring-1 ring-white/10'>
                                {match.map(([team, score], teamIndex) => {
                                    const leading = Number(score) >= Number(match[teamIndex === 0 ? 1 : 0][1])

                                    return (
                                        <div key={String(team)} className='flex items-center justify-between py-0.5 text-[11.5px]'>
                                            <span className={cn('font-semibold', leading ? 'text-white' : 'text-slate-400')}>{team}</span>
                                            <span className={cn('font-mono font-bold', leading ? 'text-emerald-300' : 'text-slate-400')}>{score}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                <Panel className='p-4'>
                    <div className='mb-2.5 flex items-center justify-between'>
                        <div className='text-[13px] font-bold'>GKV XP leaderboard</div>
                        <Flame className='size-4 text-orange-500' />
                    </div>

                    {[["Ananya Bisht", "2,840"], ["Aarav Sharma", "2,615"], ["Priya Rawat", "2,490"]].map(([name, xp], index) => (
                        <div key={name} className='flex items-center gap-2.5 border-t border-slate-100 py-2 first:border-t-0'>
                            <span className={cn('grid size-6 place-items-center rounded-full text-[10px] font-extrabold', index === 0 ? 'bg-amber-100 text-amber-700' : index === 1 ? 'bg-slate-200 text-slate-700' : 'bg-orange-100 text-orange-700')}>
                                {index + 1}
                            </span>

                            <span className='flex-1 text-[11.5px] font-semibold'>{name}</span>
                            <span className='font-mono text-[11px] font-bold text-indigo-600'>{xp} XP</span>
                        </div>
                    ))}
                </Panel>
            </div>
        </div>
    )
}

const SCREENS: Record<ModuleId, FC> = {
    'student-hub': DashboardScreen,
    journey: JourneyScreen,
    'study-vault': StudyVaultScreen,
    placements: PlacementsScreen,
    marketplace: MarketplaceScreen
}

export const LaptopScreen: FC<LaptopScreenProps> = ({ active, onSelect }): ReactNode => {
    const Screen = SCREENS[active]

    return (
        <div className='flex size-full bg-[#F6F8FB] font-sans text-[13px] text-ink antialiased'>
            <Sidebar active={active} onSelect={onSelect} />

            <div className='flex min-w-0 flex-1 flex-col'>
                <TopBar active={active} />

                <div className='relative flex-1 overflow-hidden p-6'>
                    <AnimatePresence mode='wait' initial={false}>
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                            className='h-full'
                        >
                            <Screen />
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    )
}
